import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { gql, useApolloClient } from '@apollo/client'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { pick } from 'lodash'
import { Box, CircularProgress, Stack } from '@mui/material'
import {
	AlertContext,
	Button,
	errorBanner,
	Form,
	Logger,
	useDomReady,
	toDollars,
	formDataToObject,
	parseFields
} from '@wolfdanepug/common-react'
import { AppContext } from '../../context/app/AppContext'
import { FinalizeOnboarding } from '../../graphql/docs'
import StripeCheckoutForm from './StripeCheckoutForm'
import { serializeAddress } from './utils'

const redirectUri = '/checkout/success'

export default function StripeCheckout({ cart, data, state: initialState = {}, saveState }: any) {
	Logger.log('[StripeCheckout]', cart, data)
	const RETURN_URL = `${window.location.origin}${redirectUri}`
	const navigate = useNavigate()

	const { setLoading: setAppLoading } = useContext(AppContext)
	const { pushAlert } = useContext(AlertContext)

	const [loading, setLoading] = useState<boolean>(false)
	const [addressComplete, setAddressComplete] = useState<boolean>(false)
	const [paymentMethodComplete, setPaymentMethodComplete] = useState<boolean>(false)
	// const apollo = useApolloClient()

	useEffect(() => {
		setAppLoading(loading)
	}, [loading])

	useEffect(() => {
		Logger.log('StripeCheckout cart', cart)
	}, [cart])

	useEffect(() => {
		Logger.log('StripeCheckout data', data)
	}, [data])

	const stripe = useStripe()
	const elements = useElements()
	const apollo = useApolloClient()

	/**
	 * Handle Element Change
	 * @param {string}
	 * @param {any}
	 * @returns {void}
	 */
	const handleElementChange = (element: string, { complete, value }: any): void => {
		Logger.log('[StripeCheckout] handleElementChange %s', element, complete, value)
		if (!complete) return

		switch (element) {
			case 'address':
				setAddressComplete(complete)
				break
			case 'payment':
				setPaymentMethodComplete(complete)
				break
		}
	}

	/**
	 * Handle Checkout Error
	 * @param {any} e
	 * @returns {void}
	 */
	const handleCheckoutError = (e: any): void => {
		Logger.error('handleSuccess error', e)
		pushAlert(
			errorBanner({
				title: 'Error',
				message: 'Oops! Something went wrong with your order'
			})
		)
	}

	/**
	 * Handle Checkout Success
	 * @param {any} order
	 * @param {string} token
	 * @returns {void}
	 */
	const handleCheckoutSuccess = (data: any): void => {
		Logger.log('handleSuccess', data)
		saveState(null)
		// redirect to success page
		navigate(redirectUri)
	}

	const checkout = async (input: any): Promise<any> => {
		try {
			const { data } = await apollo.mutate({
				mutation: gql(FinalizeOnboarding),
				variables: { input }
			})
			return data.finalizeOnboarding
		} catch (e) {
			handleCheckoutError(e)
		}
	}

	/**
	 * Handle Checkout Request
	 * @param {any} intent.id - Transaction ID
	 * @param {any} intent.shipping - Shipping Address
	 * @param {any} intent.payment_method - Payment Method
	 * @param {any} data
	 * @returns {Promise<void>}
	 */
	const handleCheckout = async (
		{ id, shipping, payment_method }: any,
		data: any
	): Promise<void> => {
		setLoading(true)
		const checkoutData = {
			email: data.email.trim() || '',
			name: shipping?.name.trim() || '',
			phone: shipping?.phone.trim() || '',
			shippingAddress: serializeAddress(shipping?.address)
		}
		try {
			const response: any = await checkout({
				...data,
				...checkoutData,
				tx: {
					id,
					paymentMethod: payment_method
				}
			})
			handleCheckoutSuccess(response)
		} catch (e) {
			saveState(checkoutData)
			handleCheckoutError(e)
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Handle Retry Request
	 * @returns {void}
	 */
	const handleRetryRequest = (data: any): void => {
		Logger.log('handleRetryRequest', data)
		saveState(data)
		// router.refresh()
	}

	/**
	 * Handle Payment Error
	 * @param {any} data
	 * @returns {void}
	 */
	const handlePaymentError = (data: any, formData: any): void => {
		// Show error to your customer (for example, payment details incomplete)
		pushAlert(
			errorBanner({
				title: 'Payment Failed',
				message: data.message,
				timeout: null,
				action: {
					label: 'Retry',
					onClick: () =>
						handleRetryRequest({
							...pick(data.payment_intent.shipping, ['name', 'phone', 'address']),
							email: formData.email,
							paymentMethod: data.payment_method?.id || ''
						})
				}
			})
		)
	}

	/**
	 * Confirm Payment
	 * @param {any}
	 * @returns {Promise<void>}
	 */
	const confirmPayment = async (e: any): Promise<void> => {
		e.preventDefault()
		const raw = formDataToObject(new FormData(e.target))
		const data = parseFields(raw)
		Logger.log('[StripeCheckout] confirmPayment', data)
		setLoading(true)
		if (!stripe || !elements) {
			return
		}
		const result: any = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: RETURN_URL
			},
			redirect: 'if_required'
		})
		if (result.error) {
			handlePaymentError(result.error, data)
			setLoading(false)
		} else {
			await handleCheckout(result.paymentIntent, data)
		}
	}
	const domReady = useDomReady()

	if (!domReady) return null

	const buttonDisabled =
		!stripe || !elements || loading || !addressComplete || !paymentMethodComplete

	return (
		<Form onSubmit={confirmPayment}>
			<Stack gap={3}>
				{cart && (
					<StripeCheckoutForm
						onChange={handleElementChange}
						initialState={{
							...initialState,
							name: data.name || '',
							email: data.email || ''
						}}
					/>
				)}

				<Box>
					<Button
						variant="contained"
						size="large"
						color="primary"
						type="submit"
						disabled={buttonDisabled}
						sx={{
							fontSize: '1.2rem',
							fontWeight: 600
						}}
					>
						{loading && <CircularProgress size={20} sx={{ mr: 2 }} />}Start your first gourmet box
						for $ {toDollars(cart?.total || 0)}
					</Button>
				</Box>
			</Stack>
		</Form>
	)
}
